import React, { useEffect } from 'react'
import * as Select from '@radix-ui/react-select'
import { Check } from 'lucide-react'
import { api } from 'src/api/root'
import { Tenant } from 'src/types/tenant'
import { useTenantStore } from 'src/enviroment/store/tenentStore'
import { useQueryClient } from '@tanstack/react-query'
import { memberRouterKeys } from 'src/api/routes/members'
import { projectRouterKeys } from 'src/api/routes/project'
import { IconDirection, IconLeaf } from '@tabler/icons-react'
import Typography from '../atoms/Typography'
import TooltipWrapper from '../atoms/TooltipWrapper'
import { SubscriptionTags } from '../../enviroment/api/services/subscriptionsApi'
import { cn } from '../../enviroment/lib/utils'

const getInitials = (name: string) => name.slice(0, 3).toUpperCase()

interface SectionHeaderProps {
  isCollapsed?: boolean
}

const SectionHeader: React.FC<SectionHeaderProps> = ({ isCollapsed }) => {
  const { data: tenants, isLoading } = api.tenant.myTenants.useQuery()
  const { currentTenant, setCurrentTenant } = useTenantStore()
  const queryClient = useQueryClient()

  useEffect(() => {
    if (tenants && tenants.length > 0 && !currentTenant) {
      setCurrentTenant(tenants[0])
    }
  }, [tenants, currentTenant, setCurrentTenant])

  const handleTenantChange = (selectedId: string) => {
    const selectedTenant = tenants?.find((t: Tenant) => t.id === selectedId)
    if (selectedTenant) {
      setCurrentTenant(selectedTenant as any)

      queryClient.invalidateQueries({
        queryKey: [memberRouterKeys.list, projectRouterKeys.list, SubscriptionTags.Get],
      })
    }
  }

  if (isLoading || !tenants?.length || !currentTenant) {
    return null
  }

  const displayName = isCollapsed ? getInitials(currentTenant.name) : currentTenant.name

  return (
    <div className={cn('flex flex-col justify-between px-4', isCollapsed ? 'mb-14 items-center' : 'mb-8 items-start')}>
      {!isCollapsed && (
        <Typography variant="xs" className="mb-4">
          SELECT WORKSPACE
        </Typography>
      )}
      <Select.Root value={currentTenant.id} onValueChange={handleTenantChange}>
        <Select.Trigger
          className={`inline-flex items-center rounded-lg border border-gray-700 bg-gray-800 text-sm text-light-green-50 hover:bg-gray-700 focus:outline-none ${
            isCollapsed ? 'h-10 w-10 items-center justify-center' : 'h-10 w-full justify-between p-2'
          }`}
          aria-label="Select workspace"
        >
          {isCollapsed ? (
            <Select.Value>
              <IconDirection className="h-6 w-6 text-light-green-500" />
            </Select.Value>
          ) : (
            <>
              <TooltipWrapper content={currentTenant.name} side="top" delayDuration={500}>
                <div className="w-0 min-w-0 flex-1 grow overflow-hidden text-left">
                  <Select.Value asChild>
                    <span className="block truncate">{displayName || 'Select workspace'}</span>
                  </Select.Value>
                </div>
              </TooltipWrapper>
              <Select.Icon className="flex-shrink-0">
                <IconDirection className="h-6 w-6 text-light-green-500" />
              </Select.Icon>
            </>
          )}
        </Select.Trigger>

        <Select.Portal>
          <Select.Content className="z-50 min-w-[200px] overflow-hidden rounded-md border border-gray-700 bg-gray-800 text-light-green-50 shadow-md animate-in fade-in-80">
            <Select.Viewport className="p-1">
              {tenants.map((tenant: Tenant) => (
                <Select.Item
                  key={tenant.id}
                  value={tenant.id}
                  className="relative flex h-9 cursor-pointer select-none items-center rounded px-2 py-1.5 text-sm outline-none hover:bg-gray-700 hover:text-light-green-50 focus:bg-gray-700"
                >
                  <div className="flex w-full items-center justify-between gap-2">
                    <Select.ItemText className="min-w-0 flex-1 truncate">{tenant.name}</Select.ItemText>
                    {currentTenant.id === tenant.id && <Check className="h-4 w-4 flex-shrink-0 text-light-green-500" />}
                  </div>
                </Select.Item>
              ))}
            </Select.Viewport>
          </Select.Content>
        </Select.Portal>
      </Select.Root>
    </div>
  )
}

export default SectionHeader
